import React, { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { PermissionGate } from '@/components/auth/permission-gate'
import { useAuth } from '@/lib/contexts/auth-context'
import { Logo } from '@/components/brand/Logo'
import { LogoWordmark } from '@/components/brand/LogoWordmark'
import { useBinCount } from '@/lib/hooks/use-bin-count'
import { analyticsService } from '@/lib/api/services/analytics'
import {
  LayoutDashboard,
  FolderOpen,
  FolderTree,
  Layers,
  Users,
  Settings,
  ChevronRight,
  Sparkles,
  BarChart3,
  Shield,
  CreditCard,
  LogOut,
  Building2,
  UserCircle,
  Package,
  Workflow,
  HelpCircle,
  X,
  Database,
  Palette,
  GitBranch,
  ClipboardCheck,
  ExternalLink,
  Trash2,
  Boxes,
  UserPlus,
  Lightbulb,
  FileStack,
} from 'lucide-react'

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  permissions?: string[]
  children?: NavItem[]
  badge?: string | number
}

const navigation: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    label: 'Templates',
    href: '/templates',
    icon: Layers,
    // No permissions - accessible to all authenticated users
  },
  {
    label: 'Documents',
    href: '/documents',
    icon: FileStack,
    // No permissions - accessible to all authenticated users
  },
  {
    label: 'Assets',
    href: '/assets',
    icon: FolderOpen,
    // No permissions - accessible to all authenticated users
  },
  {
    label: 'Content',
    href: '/content-hub',
    icon: Database,
    // No permissions - accessible to all authenticated users
  },
  {
    label: 'AI Studio',
    href: '/ai-studio',
    icon: Sparkles,
    // No permissions - accessible to all authenticated users
    children: [
      {
        label: 'Agents',
        href: '/ai-studio/agents',
        icon: Users,
      },
      {
        label: 'Workflows',
        href: '/ai-studio/workflows',
        icon: Workflow,
      },
      {
        label: 'Industry Presets',
        href: '/ai-studio/presets',
        icon: Boxes,
      },
    ],
  },
  {
    label: 'My Approvals',
    href: '/approval/pending',
    icon: ClipboardCheck,
    // No permissions - accessible to all authenticated users (shows their own pending approvals)
  },
  {
    label: 'Bin',
    href: '/bin',
    icon: Trash2,
  },
  {
    label: 'Administration',
    href: '/admin',
    icon: Shield,
    // Admin section - requires admin permissions to see
    permissions: ['system.admin', 'tenant.admin', 'user.view', 'team.view', 'role.view', 'integration.view', 'datasource.view', 'brandguideline.view', 'content.admin', 'analytics.view', 'workflow.admin'],
    children: [
      {
        label: 'Analytics',
        href: '/admin/analytics',
        icon: BarChart3,
        permissions: ['analytics.view'],
      },
      {
        label: 'Companies',
        href: '/admin/companies',
        icon: Building2,
        permissions: ['system.admin'],
      },
      {
        label: 'Users',
        href: '/admin/users',
        icon: Users,
        permissions: ['user.view'],
      },
      {
        label: 'Teams',
        href: '/admin/teams',
        icon: Users,
        permissions: ['team.view'],
      },
      {
        label: 'Roles',
        href: '/admin/roles',
        icon: UserCircle,
        permissions: ['role.view'],
      },
      {
        label: 'Integrations',
        href: '/admin/integrations',
        icon: Package,
        permissions: ['integration.view'],
      },
      {
        label: 'Data Sources',
        href: '/admin/data-sources',
        icon: Database,
        permissions: ['datasource.view'],
      },
      {
        label: 'Brand Guidelines',
        href: '/admin/brand-guidelines',
        icon: Palette,
        permissions: ['brandguideline.view'],
      },
      {
        label: 'Content Hub',
        href: '/admin/content-hub-settings',
        icon: FolderTree,
        permissions: ['content.admin'],
      },
      {
        label: 'Approval Workflows',
        href: '/approval/workflows',
        icon: GitBranch,
        permissions: ['workflow.admin'],
      },
      {
        label: 'Beta Registrations',
        href: '/admin/beta-registrations',
        icon: UserPlus,
        permissions: ['system.admin'],
      },
      {
        label: 'Feature Requests',
        href: '/admin/feature-requests',
        icon: Lightbulb,
        permissions: ['system.admin'],
      },
    ],
  },
  {
    label: 'Billing',
    href: '/billing',
    icon: CreditCard,
    // No permissions - accessible to all authenticated users (shows their own billing info)
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
  },
]

interface SidebarProps {
  collapsed: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  onClose?: () => void
  className?: string
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function StorageQuota() {
  const [usage, setUsage] = useState({ usedBytes: 0, totalBytes: 0, percentage: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const data = await analyticsService.getStorageAnalytics()
        setUsage({
          usedBytes: data.totalBytesUsed,
          totalBytes: data.totalBytesLimit,
          percentage: data.usagePercentage
        })
      } catch (error) {
        console.error('Failed to fetch storage usage:', error)
        // Fallback to defaults on error
        setUsage({ usedBytes: 0, totalBytes: 50 * 1024 * 1024 * 1024, percentage: 0 })
      } finally {
        setLoading(false)
      }
    }

    fetchUsage()
    // Refresh every 5 minutes
    const interval = setInterval(fetchUsage, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const isWarning = usage.percentage > 80
  const isCritical = usage.percentage > 95

  if (loading) {
    return (
      <div className="rounded-xl bg-blue-600 dark:bg-blue-700 p-3 text-white mb-2">
        <div className="h-4 bg-blue-400/30 rounded animate-pulse" />
      </div>
    )
  }

  return (
    <Link to="/billing" className="block mb-2">
      <div className="rounded-xl bg-blue-600 dark:bg-blue-700 p-3 text-white hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium">
            {formatBytes(usage.usedBytes)} of {formatBytes(usage.totalBytes)} used
          </span>
          <ExternalLink className="h-3 w-3 opacity-70" />
        </div>
        <div className="h-1.5 bg-blue-400/30 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              isCritical
                ? 'bg-red-400'
                : isWarning
                ? 'bg-yellow-400'
                : 'bg-white'
            )}
            style={{ width: `${Math.min(usage.percentage, 100)}%` }}
          />
        </div>
      </div>
    </Link>
  )
}

export function Sidebar({ collapsed, onCollapsedChange: _onCollapsedChange, onClose, className }: SidebarProps) {
  const location = useLocation()
  const pathname = location.pathname
  const { logout } = useAuth()
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const { total: binCount } = useBinCount()

  // Auto-expand parent items when navigating to child routes
  useEffect(() => {
    navigation.forEach((item) => {
      if (item.children) {
        const childMatches = item.children.some(child => pathname.startsWith(child.href))
        if (childMatches && !expandedItems.includes(item.label)) {
          setExpandedItems(prev => [...prev, item.label])
        }
      }
    })
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation with dynamic badge for Bin
  const navigationWithBadges = useMemo(() => {
    return navigation.map((item) => {
      if (item.label === 'Bin' && binCount > 0) {
        return { ...item, badge: binCount }
      }
      return item
    })
  }, [binCount])

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    )
  }

  const isActive = (href: string) => {
    if (href === '/dashboard' && pathname === '/dashboard') return true
    if (href !== '/dashboard' && pathname.startsWith(href)) return true
    return false
  }

  const handleLogout = async () => {
    await logout()
  }

  const renderNavItem = (item: NavItem, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.label)
    const active = isActive(item.href)

    const itemContent = (
      <div
        className={cn(
          'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150',
          active
            ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-50'
            : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800/50 dark:hover:text-neutral-200',
          isChild && 'ml-7 py-1.5 text-[13px]',
          collapsed && !isChild && 'justify-center px-2'
        )}
        onClick={hasChildren ? () => toggleExpanded(item.label) : undefined}
      >
        <item.icon
          className={cn(
            'flex-shrink-0 h-[18px] w-[18px] transition-colors',
            active
              ? 'text-neutral-900 dark:text-neutral-50'
              : 'text-neutral-500 group-hover:text-neutral-700 dark:text-neutral-400 dark:group-hover:text-neutral-300',
            collapsed && !isChild && 'h-5 w-5'
          )}
        />

        {!collapsed && (
          <>
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neutral-200 px-1.5 text-xs font-medium text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300">
                {item.badge}
              </span>
            )}
            {hasChildren && (
              <ChevronRight
                className={cn(
                  'h-4 w-4 text-neutral-400 transition-transform duration-200',
                  isExpanded && 'rotate-90'
                )}
              />
            )}
          </>
        )}
      </div>
    )

    const itemWithChildren = (
      <>
        {itemContent}
        {!collapsed && hasChildren && isExpanded && (
          <div className="mt-0.5 space-y-0.5">
            {item.children?.map((child) => (
              <PermissionGate key={child.href} permissions={child.permissions}>
                {renderNavItem(child, true)}
              </PermissionGate>
            ))}
          </div>
        )}
      </>
    )

    if (hasChildren) {
      return (
        <div key={item.href} className="cursor-pointer">
          {itemWithChildren}
        </div>
      )
    }

    return (
      <Link key={item.href} to={item.href} className="block">
        {itemWithChildren}
      </Link>
    )
  }

  return (
    <div
      className={cn(
        'h-full flex flex-col bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 transition-all duration-200',
        collapsed ? 'w-[60px]' : 'w-[240px]',
        className
      )}
    >
      {/* Logo Header */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-neutral-100 dark:border-neutral-800">
        {collapsed ? (
          <Link to="/dashboard" className="mx-auto">
            <Logo variant="icon" size="sm" />
          </Link>
        ) : (
          <>
            <Link to="/dashboard" aria-label="Templatean dashboard" className="pl-1">
              <LogoWordmark size="sm" />
            </Link>
            {/* Mobile close button */}
            {onClose && (
              <button
                onClick={onClose}
                className="lg:hidden p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              >
                <X className="h-4 w-4 text-neutral-500" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-3 px-2">
        <nav className="space-y-0.5">
          {navigationWithBadges.map((item) => (
            <PermissionGate key={item.href} permissions={item.permissions}>
              {renderNavItem(item)}
            </PermissionGate>
          ))}
        </nav>
      </div>

      {/* Footer */}
      <div className="border-t border-neutral-100 dark:border-neutral-800 p-2 space-y-0.5">
        {/* Usage Quota */}
        {!collapsed && <StorageQuota />}

        <Link to="/help">
          <div
            className={cn(
              'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800/50 dark:hover:text-neutral-200',
              collapsed && 'justify-center px-2'
            )}
          >
            <HelpCircle className={cn('flex-shrink-0 text-neutral-500 group-hover:text-neutral-700 dark:text-neutral-400 dark:group-hover:text-neutral-300', collapsed ? 'h-5 w-5' : 'h-[18px] w-[18px]')} />
            {!collapsed && <span>Help & Support</span>}
          </div>
        </Link>

        <button onClick={handleLogout} className="w-full text-left">
          <div
            className={cn(
              'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-neutral-400 dark:hover:bg-red-950/30 dark:hover:text-red-400',
              collapsed && 'justify-center px-2'
            )}
          >
            <LogOut className={cn('flex-shrink-0 text-neutral-500 group-hover:text-red-500 dark:text-neutral-400 dark:group-hover:text-red-400', collapsed ? 'h-5 w-5' : 'h-[18px] w-[18px]')} />
            {!collapsed && <span>Logout</span>}
          </div>
        </button>
      </div>
    </div>
  )
}
